import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Sara() {
  const [history, setHistory] = useState([]); // {role:'user'|'assistant', content}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...history, { role: "user", content: text }];
    setHistory(next);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/agent/support-chat", { message: text, history: next.slice(0, -1) });
      setHistory((h) => [...h, { role: "assistant", content: r.data.reply || "" }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sara è offline");
      setHistory((h) => h.slice(0, -1));
      setInput(text);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 fade-slide max-w-2xl flex flex-col h-[calc(100vh-160px)]">
      <div>
        <h1 className="text-3xl font-bold text-[#0A0A0A] flex items-center gap-2">
          <MessageCircle size={26} className="text-[#B40101]" /> Sara
        </h1>
        <p className="text-gray-500 mt-1">L'assistente di supporto di ValutaGenio, sempre disponibile.</p>
      </div>

      <div className="vg-card p-4 flex-1 overflow-y-auto" ref={scrollRef} data-testid="sara-chat">
        {history.length === 0 && !loading && (
          <div className="text-center text-gray-400 text-sm py-12">
            Ciao! Sono Sara. Chiedimi qualcosa sulla dashboard, sui link referral, sul funnel, sulle fee o sul codice di condotta.
          </div>
        )}
        <div className="space-y-3">
          {history.map((m, i) => (
            <div
              key={i}
              className={`p-3 rounded-[12px] max-w-[88%] ${
                m.role === "user" ? "ml-auto bg-[#B40101] text-white" : "bg-gray-100 text-[#0A0A0A]"
              }`}
              data-testid={`sara-msg-${m.role}-${i}`}
            >
              <div className="text-xs font-bold uppercase tracking-wide mb-1 opacity-70">
                {m.role === "user" ? "Tu" : "Sara"}
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="animate-spin" size={14} /> Sara sta scrivendo…
            </div>
          )}
        </div>
      </div>

      <div className="vg-card p-3 flex gap-2 items-end" data-testid="sara-input-row">
        <textarea
          className="vg-input flex-1 min-h-[44px] max-h-[120px] resize-none"
          placeholder="Chiedimi qualcosa su ValutaGenio..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          data-testid="sara-input"
        />
        <button
          className="vg-btn-primary px-4 py-3 inline-flex items-center gap-2"
          onClick={send}
          disabled={loading || !input.trim()}
          data-testid="sara-send"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <><Send size={16} /></>}
        </button>
      </div>
    </div>
  );
}
